import { SEOHead } from "@/components/SEOHead";
import { campgroundSchema } from "@/lib/schema";
import { Header } from "@/components/layout/Header";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getPostsBySlugs } from "@/content/blogPosts";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { seoCopy } from "@/content/seoCopy";
import { Button } from "@/components/ui/button";
import { MapPin, Car, Bath, Waves, Phone, Download, Navigation } from "lucide-react";
import { Link } from "react-router-dom";
import propertyMapImage from "@/assets/property-map.webp";

const PropertyMap = () => {
  const legendItems = [
    { icon: <div className="w-4 h-4 bg-amber-700 rounded-sm" />, label: "In-Water Tables", description: "Picnic tables right in the river" },
    { icon: <div className="w-4 h-4 bg-amber-600 rounded-full border-2 border-amber-800" />, label: "Cabanas", description: "Private riverside cabanas (numbered 1-22)" },
    { icon: <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[14px] border-b-cyan-500" />, label: "Glamping Tents", description: "Luxury overnight tents (lettered A-M)" },
  ];

  const facilities = [
    { icon: Car, title: "Parking", description: "Ample parking available for all guests" },
    { icon: Bath, title: "Bathrooms & Showers", description: "Clean facilities with showers near the shuttle area" },
    { icon: MapPin, title: "Check-In Office", description: "Main entrance with 5-minute parking for quick check-in" },
    { icon: Waves, title: "River Access", description: "Direct access to the Guadalupe River from all cabanas" },
  ];

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Property Map | Son's Guadalupe on the Guadalupe River"
        description="Explore the Son's Guadalupe property map: cabanas, in-water picnic tables, glamping tents, parking & river access in New Braunfels, TX."
        canonical="/property-map"
        ogImage="/og/property-map.jpg"
        schemaJson={[
          campgroundSchema({
            "@id": "https://sonsguadalupe.com/property-map#campground",
            description:
              "Son's Guadalupe property map: 22 private riverside cabanas, in-water covered picnic tables, AC bell tent glamping, dedicated parking, and onsite bathhouse on the Guadalupe River in New Braunfels, TX.",
          }),
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
              { "@type": "ListItem", position: 2, name: "Property Map", item: "https://sonsguadalupe.com/property-map" },
            ],
          },
        ]}
      />
      <Header />
      <main className="pt-32 md:pt-36">
        {/* Hero Section */}
        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-lora text-4xl md:text-5xl font-bold text-teal mb-4">
                Property Map
              </h1>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Explore our riverside property and plan your perfect day on the Guadalupe River. 
                With 22 private cabanas and 13 glamping tents, there's a spot for everyone.
              </p>
            </div>

            {/* Map Image */}
            <div className="bg-card rounded-2xl overflow-hidden shadow-card mb-12">
              <div className="relative">
                <img 
                  src={propertyMapImage} 
                  alt="Son's Guadalupe Property Map showing cabanas, glamping tents, parking, and facilities along the Guadalupe River" 
                  className="w-full h-auto"
                />
              </div>
              <div className="p-4 bg-muted/30 border-t border-border flex flex-wrap gap-4 justify-center items-center">
                <a 
                  href={propertyMapImage} 
                  download="sons-guadalupe-property-map.webp"
                  className="inline-flex items-center gap-2 text-teal hover:underline font-medium"
                >
                  <Download className="w-4 h-4" />
                  Download Map
                </a>
                <span className="text-muted-foreground">|</span>
                <a 
                  href="https://www.google.com/maps/dir/?api=1&destination=7308+River+Road,+New+Braunfels,+TX+78132" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-teal hover:underline font-medium"
                >
                  <MapPin className="w-4 h-4" />
                  Get Directions
                </a>
              </div>
            </div>

            {/* Legend */}
            <div className="bg-card rounded-2xl p-8 shadow-card mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground mb-6 text-center">
                Map Legend
              </h2>
              <div className="grid sm:grid-cols-3 gap-6">
                {legendItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-muted/30 rounded-xl">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{item.label}</h3>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Detailed Property Descriptions */}
            <div className="mb-12">
              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-3">
                  Son's Properties at a Glance
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  Son's operates several riverside locations along the Guadalupe River and Cibolo Creek. Here's what makes each one special.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { name: "Son's Guadalupe (River Road)", location: "7308 River Rd, New Braunfels, TX", href: "/book-now/sons-guadalupe", description: "The flagship — 22 private cabanas, in-water picnic tables, glamping bell tents, and a calm spring-fed stretch of the Guadalupe River. Float right back to your cabana, no shuttles." },
                  { name: "Son's River Ranch", location: "New Braunfels, TX", href: "/book-now/river-ranch", description: "Wide-open riverside acreage with cabanas, tent camping, and overnight glamping. Beloved by groups, family reunions, and spring-break crews who want elbow room and direct river access." },
                  { name: "Son's Blue River Camp", location: "New Braunfels, TX", href: "/book-now/blue-river-camp", description: "A quieter Guadalupe River property with day cabanas, picnic tables, and overnight glamping. The river runs clear and shallow here — perfect for tubing with younger kids." },
                  { name: "Son's Rio Cibolo", location: "Marion, TX (east of New Braunfels)", href: "/book-now/rio-cibolo", description: "Cabanas and glamping on Cibolo Creek — gentler and more secluded than the main Guadalupe. Great for families with toddlers and high-water days." },
                  { name: "Son's Island", location: "Seguin, TX", href: "/book-now/sons-island", description: "Texas's only private spring-fed island getaway — cabanas and glamping on a clear, calm lake. Swimming, paddleboarding, and all-day relaxation." },
                  { name: "Son's Geronimo", location: "Seguin, TX", href: "/book-now/geronimo", description: "Day rentals along Geronimo Creek — cabanas, picnic tables, and a quiet creek-side vibe close to San Antonio." },
                ].map((p) => (
                  <Link key={p.name} to={p.href} className="bg-card rounded-2xl p-6 shadow-card hover:shadow-elevated transition-shadow group block">
                    <h3 className="font-heading text-xl font-bold text-foreground group-hover:text-teal transition-colors">{p.name}</h3>
                    <p className="text-sm text-teal mb-3 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.location}</p>
                    <p className="text-muted-foreground text-sm leading-relaxed">{p.description}</p>
                    <p className="mt-4 text-sm text-teal font-medium">Book this property →</p>
                  </Link>
                ))}
              </div>
            </div>

            {/* Facilities Grid */}
            <div className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-foreground mb-6 text-center">
                Property Facilities
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {facilities.map((facility, index) => (
                  <div key={index} className="bg-card rounded-xl p-6 shadow-card text-center">
                    <div className="w-14 h-14 rounded-full bg-teal/10 flex items-center justify-center mx-auto mb-4">
                      <facility.icon className="w-7 h-7 text-teal" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{facility.title}</h3>
                    <p className="text-sm text-muted-foreground">{facility.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Location Info with Map */}
            <div className="bg-deep-green rounded-2xl overflow-hidden text-white">
              <div className="p-8 text-center">
                <h2 className="font-heading text-2xl font-bold mb-4">
                  Find Us on River Road
                </h2>
                <p className="text-white/90 mb-2 text-lg">
                  7308 River Road, New Braunfels, TX 78132
                </p>
                <p className="text-white/70 mb-6">
                  Look for our sign at the entrance • Easy access from I-35
                </p>
                <div className="flex flex-wrap gap-4 justify-center mb-6">
                  <Button 
                    variant="warm" 
                    size="lg"
                    onClick={() => window.open('https://www.google.com/maps/dir/?api=1&destination=7308+River+Road,+New+Braunfels,+TX+78132', '_blank')}
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Get Directions
                  </Button>
                  <Button variant="hero-outline" size="lg" asChild>
                    <a href="tel:877-577-7667">
                      <Phone className="w-4 h-4 mr-2" />
                      877-577-SONS
                    </a>
                  </Button>
                </div>
              </div>
              <div className="aspect-[16/9] md:aspect-[21/9]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3467.8!2d-98.1456!3d29.7199!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x865cbd1b8b8b8b8b%3A0x8b8b8b8b8b8b8b8b!2s7308+River+Rd%2C+New+Braunfels%2C+TX+78132!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Son's Guadalupe Location"
                  className="w-full h-full"
                />
              </div>
            </div>
          </div>
        </section>
      </main>
      <SEOContentBlock {...seoCopy.propertyMap} />
      <RelatedReads posts={getPostsBySlugs(["river-road-new-braunfels-guide", "memorial-day-weekend-guadalupe-river", "tubing-guadalupe-river-new-braunfels"])} heading="From our blog" />
      <Footer />
    </div>
  );
};

export default PropertyMap;
